import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import CopyIcon from '../icons/CopyIcon'; // Import CopyIcon
import { copyToClipboard } from '../../utils/textUtils'; // Import copyToClipboard

const MenuContainer = styled.div`
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    border-radius: 4px;
    padding: 4px;
    min-width: 160px;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const MenuItemContainer = styled.div`
    position: relative;
`;

const MenuItem = styled.button`
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: #B6C2CF;
    text-align: left;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }

    &.delete:hover {
        color: #ff4757;
    }
`;

const MenuTrigger = styled.button`
    background: none;
    border: none;
    color: #B6C2CF;
    padding: 4px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 102;

    &:hover {
        color: #fff;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const MenuWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 102;
`;

const MoveToSubMenu = styled(MenuContainer)`
    top: 0;
    left: 100%;
    margin-left: 4px;
`;

// Add saveContent to the component props
const SaveMenu = ({
    onDelete,
    onToggleArchive,
    isArchived,
    onEditTitle,
    isRead,
    onMarkAsRead,
    onMarkAsUnread,
    saveContent, // Prop for the content to be copied
    onEditTags, // New prop for editing tags
    onMoveToTab,
    onMoveToBookmarks,
    customTabs,
    // Props for state lifting
    isOpen,
    onToggle,
    saveId
}) => {
    const [showMoveToSubMenu, setShowMoveToSubMenu] = React.useState(false);
    const [menuPosition, setMenuPosition] = React.useState(null);

    const handleAction = (actionFn) => {
        if (actionFn) {
            actionFn();
        }
        if (onToggle) {
            onToggle(); // Close menu after action
        }
    };

    const handleDelete = () => handleAction(onDelete);
    const handleEditTagsClick = () => handleAction(onEditTags);
    const handleArchive = () => handleAction(onToggleArchive);
    const handleEditTitle = () => handleAction(onEditTitle);
    const handleMarkAsReadClick = () => handleAction(onMarkAsRead);
    const handleMarkAsUnreadClick = () => handleAction(onMarkAsUnread);

    const handleCopy = () => {
      if (saveContent) {
        copyToClipboard(saveContent);
      } else {
        console.warn('No content provided to copy for save item.');
        // alert('No content to copy.'); // Consider less intrusive feedback
      }
      if (onToggle) {
          onToggle(); // Close menu
      }
    };

    const menuRef = React.useRef(null); // Ref for the menu container
    const triggerRef = React.useRef(null); // Ref for the trigger button

    React.useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + window.scrollY,
                left: rect.right + window.scrollX - 160, // Align right edge of menu
            });
        }
    }, [isOpen]);

    // Combined click outside and escape key handler
    React.useEffect(() => {
        const handleClickOutsideAndEscape = (event) => {
            if (isOpen) {
                if (event.type === 'keydown' && event.key === 'Escape') {
                    if (onToggle) onToggle();
                } else if (event.type === 'mousedown') {
                    // If the click is on the trigger itself, let the trigger's onClick handle it.
                    if (triggerRef.current && triggerRef.current.contains(event.target)) {
                        return;
                    }
                    // If the click is outside the menu container
                    if (menuRef.current && !menuRef.current.contains(event.target)) {
                        if (onToggle) onToggle();
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutsideAndEscape);
        document.addEventListener('keydown', handleClickOutsideAndEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideAndEscape);
            document.removeEventListener('keydown', handleClickOutsideAndEscape);
        };
    }, [isOpen, onToggle]);

    const menuContent = isOpen && menuPosition && (
        <MenuContainer
            ref={menuRef}
            style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
            }}
        >
            <MenuItem onClick={handleEditTitle}>
                Edit title
            </MenuItem>
            <MenuItem onClick={handleEditTagsClick}>
                Edit Tags
            </MenuItem>
            <MenuItemContainer onMouseEnter={() => setShowMoveToSubMenu(true)} onMouseLeave={() => setShowMoveToSubMenu(false)}>
                <MenuItem>
                    Move to
                </MenuItem>
                {showMoveToSubMenu && (
                    <MoveToSubMenu>
                        <MenuItem onClick={() => handleAction(onMoveToBookmarks)}>
                            Bookmarks
                        </MenuItem>
                        <MenuItem onClick={() => handleAction(() => onMoveToTab(saveId, null))}>
                            All
                        </MenuItem>
                        {customTabs.map(tab => (
                            <MenuItem key={tab.id} onClick={() => handleAction(() => onMoveToTab(saveId, tab.id))}>
                                {tab.name}
                            </MenuItem>
                        ))}
                    </MoveToSubMenu>
                )}
            </MenuItemContainer>
            <MenuItem onClick={handleCopy}>
                <CopyIcon /> Copy Content
            </MenuItem>
            {isRead ? (
                <MenuItem onClick={handleMarkAsUnreadClick}>
                    Mark as Unread
                </MenuItem>
            ) : (
                <MenuItem onClick={handleMarkAsReadClick}>
                    Mark as Read
                </MenuItem>
            )}
            <MenuItem onClick={handleArchive}>
                {isArchived ? 'Unarchive' : 'Archive'}
            </MenuItem>
            <MenuItem className="delete" onClick={handleDelete}>
                Delete
            </MenuItem>
        </MenuContainer>
    );

    return (
        <MenuWrapper className="menu-wrapper">
            <MenuTrigger onClick={onToggle} className="menu-trigger" ref={triggerRef}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                </svg>
            </MenuTrigger>
            {isOpen && menuPosition && ReactDOM.createPortal(menuContent, document.body)}
        </MenuWrapper>
    );
};

export default SaveMenu;